"use client";

import { useState, useEffect } from "react";
import { Bell, Check, X } from "lucide-react";
import { useAuth } from "@/lib/hooks/useAuth";
import { requestNotificationPermission, estAppliNative } from "@/lib/firebase/messaging";

export default function NotificationPrompt() {
  const { user } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if ("Notification" in window) {
      setPermission(Notification.permission);
    }
    
    // Deja autorise : on RE-ENREGISTRE le telephone a chaque ouverture.
    // Sans ca, un jeton perime (mise a jour iOS, appli reinstallee, jeton
    // supprime cote serveur) n'etait jamais remplace : plus aucune alerte,
    // sans que personne ne le sache.
    // Appli Android : pas de bandeau, l'appareil s'enregistre tout seul
    // (Android demande lui-meme l'autorisation la premiere fois).
    if (estAppliNative()) {
      if (user?.uid) requestNotificationPermission(user.uid).catch(() => {});
      setDismissed(true);
      return;
    }
    if ("Notification" in window && Notification.permission === "granted" && user?.uid) {
      requestNotificationPermission(user.uid).catch(() => {});
    }

    // Vérifier si déjà dismissé dans cette session
    const wasDismissed = sessionStorage.getItem("notifPromptDismissed");
    if (wasDismissed === "true") {
      setDismissed(true);
    }
  }, [user?.uid]);

  const handleEnable = async () => {
    if (!user?.uid) return;
    
    setLoading(true);
    try {
      const token = await requestNotificationPermission(user.uid);
      if (token) {
        setPermission("granted");
        console.log("✅ Notifications activées!");
      } else {
        setPermission("denied");
      }
    } catch (error) {
      console.error("❌ Erreur activation notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("notifPromptDismissed", "true");
  };

  // Ne rien afficher si déjà accordée ou refusée ou dismissé
  if (permission !== "default" || dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-20 md:bottom-4 right-4 left-4 md:left-auto md:w-96 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 z-50 animate-in slide-in-from-bottom-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
          <Bell className="w-5 h-5 text-blue-600" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 mb-1">
            Activer les notifications
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Recevez des alertes instantanées pour les nouvelles commandes et messages
          </p>
          
          <div className="flex gap-2">
            <button
              onClick={handleEnable}
              disabled={loading}
              className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="animate-spin">⏳</span>
              ) : (
                <Check className="w-4 h-4" />
              )}
              Activer
            </button>
            
            <button
              onClick={handleDismiss}
              className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors"
            >
              Plus tard
            </button>
          </div>
        </div>
        
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
